import * as React from 'react';
import { cn } from '@/util/cn';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      '$tw-fixed $tw-inset-0 $tw-z-50 $tw-bg-black/80 data-[state=open]:$tw-animate-in data-[state=closed]:$tw-animate-out data-[state=closed]:$tw-fade-out-0 data-[state=open]:$tw-fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        '$tw-fixed $tw-left-[50%] $tw-top-[50%] $tw-z-50 $tw-grid $tw-max-h-[95%] $tw-w-full $tw-max-w-lg $tw-translate-x-[-50%] $tw-translate-y-[-50%] $tw-gap-4 $tw-overflow-y-auto $tw-border $tw-bg-background $tw-p-6 $tw-text-foreground $tw-shadow-lg $tw-duration-200 data-[state=open]:$tw-animate-in data-[state=closed]:$tw-animate-out data-[state=closed]:$tw-fade-out-0 data-[state=open]:$tw-fade-in-0 data-[state=closed]:$tw-zoom-out-95 data-[state=open]:$tw-zoom-in-95 data-[state=closed]:$tw-slide-out-to-left-1/2 data-[state=closed]:$tw-slide-out-to-top-[48%] data-[state=open]:$tw-slide-in-from-left-1/2 data-[state=open]:$tw-slide-in-from-top-[48%] sm:$tw-rounded-lg',
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="$tw-absolute $tw-right-4 $tw-top-4 $tw-rounded-sm $tw-opacity-70 $tw-ring-offset-background $tw-transition-opacity hover:$tw-opacity-100 focus:$tw-outline-none focus:$tw-ring-2 focus:$tw-ring-ring focus:$tw-ring-offset-2 disabled:$tw-pointer-events-none data-[state=open]:$tw-bg-accent data-[state=open]:$tw-text-muted-foreground">
        <X className="$tw-h-4 $tw-w-4 $tw-text-foreground" />
        <span className="$tw-sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      '$tw-flex $tw-flex-col $tw-space-y-1.5 $tw-text-center sm:$tw-text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      '$tw-flex $tw-flex-col-reverse sm:$tw-flex-row sm:$tw-justify-end sm:$tw-space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      '$tw-text-3xl $tw-font-semibold $tw-leading-none $tw-tracking-tight $tw-text-foreground',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('$tw-text-sm $tw-text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
